import React, { useEffect, useState } from 'react';
import { Sidebar } from '../../../components';
import {
  AccountTreeTwoTone,
  FileCopy,
  SettingsApplicationsRounded,
  ToysOutlined,
  Portrait,
} from '@mui/icons-material';

import { UserList } from './RoleManagement/UserList';
import styled from 'styled-components';
import { ConfigurationList } from './ConfigurationManagement/ConfigurationList';
import { OrganizationManagement } from '../OrganizationManagement';
import { AdhocHomePage } from './AdhocOperation/AdhocHomePage';
import { ApplicantInformationUpdateHomePage } from './ApplicantInformation/ApplicantInformationHomePage';
import { useLocation, useNavigate } from 'react-router-dom';

enum CONTENT_ID {
  ROLE_MANAGEMENT = 'ROLE_MANAGEMENT',
  CONFIGURATION_MANAGEMENT = 'CONFIGURATION_MANAGEMENT',
  ORGANIZATION_MANAGEMENT = 'ORGANIZATION_MANAGEMENT',
  AD_HOC_TOOLS = 'AD_HOC_TOOLS',
  APPLICANT_INFORMATION_UPDATE = 'APPLICANT_INFORMATION_UPDATE',
}

type Module = {
  id: CONTENT_ID;
  title: string;
  component: React.ReactNode;
};

type Url = {
  id: CONTENT_ID;
  url: string;
  navigate: boolean;
};

const moduleMapping: Record<CONTENT_ID, Module> = {
  [CONTENT_ID.ROLE_MANAGEMENT]: {
    id: CONTENT_ID.ROLE_MANAGEMENT,
    title: 'Role Management',
    component: <UserList />,
  },

  [CONTENT_ID.CONFIGURATION_MANAGEMENT]: {
    id: CONTENT_ID.CONFIGURATION_MANAGEMENT,
    title: 'Configuration Management',
    component: <ConfigurationList />,
  },

  [CONTENT_ID.ORGANIZATION_MANAGEMENT]: {
    id: CONTENT_ID.ORGANIZATION_MANAGEMENT,
    title: 'Organization Management',
    component: <OrganizationManagement />,
  },

  [CONTENT_ID.AD_HOC_TOOLS]: {
    id: CONTENT_ID.AD_HOC_TOOLS,
    title: 'Ad-hoc Tools',
    component: <AdhocHomePage />,
  },

  [CONTENT_ID.APPLICANT_INFORMATION_UPDATE]: {
    id: CONTENT_ID.APPLICANT_INFORMATION_UPDATE,
    title: 'Applicant Information Update',
    component: <ApplicantInformationUpdateHomePage />,
  },
};

// better NOT to put moduleMapping and urlMapping together
const urlMapping: Record<CONTENT_ID, Url> = {
  [CONTENT_ID.ROLE_MANAGEMENT]: {
    id: CONTENT_ID.ROLE_MANAGEMENT,
    url: 'user-management',
    navigate: true,
  },

  [CONTENT_ID.CONFIGURATION_MANAGEMENT]: {
    id: CONTENT_ID.CONFIGURATION_MANAGEMENT,
    url: 'configuration-management',
    navigate: true,
  },

  [CONTENT_ID.ORGANIZATION_MANAGEMENT]: {
    id: CONTENT_ID.ORGANIZATION_MANAGEMENT,
    url: 'organization-management',
    navigate: false,
  },

  [CONTENT_ID.AD_HOC_TOOLS]: {
    id: CONTENT_ID.AD_HOC_TOOLS,
    url: 'adhoc-tools',
    navigate: true,
  },

  [CONTENT_ID.APPLICANT_INFORMATION_UPDATE]: {
    id: CONTENT_ID.APPLICANT_INFORMATION_UPDATE,
    url: 'applicant-information-update',
    navigate: true,
  },
};

const superAdminMonitorSidebarData = [
  {
    id: CONTENT_ID.ROLE_MANAGEMENT,
    name: moduleMapping[CONTENT_ID.ROLE_MANAGEMENT].title,
    icon: <FileCopy />,
  },
  {
    id: CONTENT_ID.CONFIGURATION_MANAGEMENT,
    name: moduleMapping[CONTENT_ID.CONFIGURATION_MANAGEMENT].title,
    icon: <SettingsApplicationsRounded />,
  },
  {
    id: CONTENT_ID.ORGANIZATION_MANAGEMENT,
    name: moduleMapping[CONTENT_ID.ORGANIZATION_MANAGEMENT].title,
    icon: <AccountTreeTwoTone />,
  },
  {
    id: CONTENT_ID.AD_HOC_TOOLS,
    name: moduleMapping[CONTENT_ID.AD_HOC_TOOLS].title,
    icon: <ToysOutlined />,
  },
  {
    id: CONTENT_ID.APPLICANT_INFORMATION_UPDATE,
    name: moduleMapping[CONTENT_ID.APPLICANT_INFORMATION_UPDATE].title,
    icon: <Portrait />,
  },
];

export const SuperAdminMonitor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activatedId, setActivatedId] = useState<string>(
    CONTENT_ID.ROLE_MANAGEMENT,
  );

  const activatedModule = moduleMapping[activatedId as CONTENT_ID];

  const handleEnterUrl = (): void => {
    const target = Object.values(urlMapping).find(
      item => item.url === location.pathname.split('/').at(-1),
    );
    setActivatedId(target?.id as CONTENT_ID);
  };

  const handleOnchangeActivated = (activatedId: string): void => {
    const target = urlMapping[activatedId as CONTENT_ID];
    const url = `${location.pathname.split('/').slice(0, -1).join('/')}/${target.url}`;

    if (target.navigate) {
      navigate(url, { replace: true });
    } else {
      window.history.replaceState(null, '', url);
    }
    setActivatedId(activatedId);
  };

  useEffect(() => {
    handleEnterUrl();
  }, []);

  return (
    <SuperAdminMonitorStyle>
      <SidebarStyle>
        <Sidebar
          sidebarData={superAdminMonitorSidebarData}
          onChangeActivated={handleOnchangeActivated}
          manuallyActivatedId={activatedId}
        />
      </SidebarStyle>
      <ContentStyle>
        <h1>{activatedModule.title}</h1>
        {activatedModule.component}
      </ContentStyle>
    </SuperAdminMonitorStyle>
  );
};

const SuperAdminMonitorStyle = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const SidebarStyle = styled.div`
  margin-top: 1rem;
  height: 100%;
  width: min-content;
`;

const ContentStyle = styled.div`
  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }

  overflow-x: scroll;
  margin: auto;
  width: 100%;
`;
